import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Twitter } from 'lucide-react';
import { BRAND } from '../../data/mock';

const Footer = () => {
  return (
    <footer className="bg-[#2F5233] text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <div className="rounded-xl overflow-hidden border border-white/20 h-28 bg-white/10">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3503.7847076768373!2d77.33615757549373!3d28.568629075692714!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce5b6632b240d%3A0x4c0eb8fee56fca8a!2sDr.%20Jenice%20Bhatia%20Clinic!5e0!3m2!1sen!2sin!4v1705331000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Dr. Jenice Bhatia Clinic Location"
              />
            </div>
            <p className="text-white/80 text-sm leading-relaxed">
              Experience holistic healing through classical homoeopathy. 
              We treat the person, not just the disease.
            </p>
            <a
              href="https://www.google.com/maps/place/Dr.+Jenice+Bhatia+Clinic/@28.568629,77.3361576,17z"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-sm text-[#F5A623] hover:underline"
            >
              View clinic location on Google Maps
            </a>
            <div className="flex space-x-4">
              <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#E91E8C]/30 transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#E91E8C]/30 transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#E91E8C]/30 transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 font-serif">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: 'Home', path: '/' },
                { name: 'About Us', path: '/about' },
                { name: 'Services', path: '/services' },
                { name: 'Book Appointment', path: '/book' },
                { name: 'Contact', path: '/contact' }
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/80 hover:text-[#F5A623] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6 font-serif">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-[#F5A623] mt-0.5" />
                <div>
                  <p className="text-sm text-white/70">Phone</p>
                  <a href="tel:+917353196539" className="text-white hover:text-[#F5A623]">+91 73531 96539</a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-[#F5A623] mt-0.5" />
                <div>
                  <p className="text-sm text-white/70">Email</p>
                  <a href="mailto:drjenicebhatia@gmail.com" className="text-white hover:text-[#F5A623]">drjenicebhatia@gmail.com</a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-[#F5A623] mt-0.5" />
                <div>
                  <p className="text-sm text-white/70">Address</p>
                  <a href={BRAND.clinics[0].mapUrl} target="_blank" rel="noopener noreferrer" className="block text-white hover:text-[#F5A623]">
                    {BRAND.clinics[0].full}
                  </a>
                  <a href={BRAND.clinics[1].mapUrl} target="_blank" rel="noopener noreferrer" className="mt-2 block text-white hover:text-[#F5A623]">
                    {BRAND.clinics[1].full}
                  </a>
                </div>
              </li>
            </ul>
          </div>

          {/* Clinic Hours */}
          <div>
            <h4 className="text-lg font-semibold mb-6 font-serif">Clinic Hours</h4>
            <ul className="space-y-3">
              <li className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-[#F5A623]" />
                <span className="text-white/80 text-sm">Mon, Wed - Fri</span>
              </li>
              <li className="pl-8">
                <p className="text-white font-medium">11:00 AM - 2:00 PM & 4:00 PM - 7:00 PM</p>
              </li>
              <li className="flex items-center space-x-3 mt-2">
                <Clock className="w-5 h-5 text-[#F5A623]" />
                <span className="text-white/80 text-sm">Sat & Sun</span>
              </li>
              <li className="pl-8">
                <p className="text-white font-medium">11:00 AM - 3:00 PM</p>
              </li>
              <li className="flex items-center space-x-3 mt-4">
                <div className="w-5 h-5 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-[#E91E8C]"></div>
                </div>
                <span className="text-white/70 text-sm">Closed on Tuesdays</span>
              </li>
            </ul>
            <div className="mt-6 p-4 bg-white/10 rounded-xl">
              <p className="text-sm text-white/90">
                <span className="font-medium text-[#F5A623]">Online:</span> ₹1,200
              </p>
              <p className="text-sm text-white/90 mt-1">
                <span className="font-medium text-[#F5A623]">In-Clinic:</span> ₹1,200
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <p className="text-sm text-white/60">
              © {new Date().getFullYear()} {BRAND.name}. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-sm text-white/60 hover:text-[#F5A623] transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-white/60 hover:text-[#F5A623] transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
